import { useCartQuery } from '@graphql/queries/__generated__/cart.generated'
import { useMeQuery } from '@graphql/queries/__generated__/me.generated'
import CloseIcon from '@mui/icons-material/Close'
import { Divider } from '@mui/material'
import Grid from '@mui/material/Grid'
import Hidden from '@mui/material/Hidden'
import useScrollPosition from '@react-hook/window-scroll'
import Cookies from 'js-cookie'
import { useSnackbar } from 'notistack'
import qs from 'qs'
import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useQueryParams } from 'use-query-params'
import SearchIcon from '../../../icons/SearchIcon'
import useLocalStorage from '../../hooks/useLocalStorage'
import useScrollDirection, { ScrollDirection } from '../../hooks/useScrollDirection'
import { SEARCH_URL } from '../../urls'
import { GlobalDialogFormsParams } from '../GlobalDialogForms'

import HeaderLogo from './components/HeaderLogo'
import HeaderMobileSearch from './components/HeaderMobileSearch'
import HeaderUtilities from './components/HeaderUtilities'
import Menu from './Menu/Menu'
import { headerOffsetAnimatedVar, headerOffsetVar, isWholesaleModeVar } from '../../../apolloClient'
import { COOKIE_NAMES, LOCAL_STORAGE_KEYS } from '../../../constants'
import { useReactiveVar } from '@apollo/client'
import { CartTypeEnum } from '../../../generated/gqlTypes'
import HeaderSearch from '@components/Header/components/HeaderSearch'
import {
  BACKEND_ENUM_TO_IMG_SIZE,
  DEFAULT_IMG_SIZE,
  REFLECTIONS_IMG_SIZE_ENUM,
  STORAGE_GRID_NAME,
} from '../../Pages/Category/SpecificsList'

const MobileMenu = React.lazy(() => import('./Menu/MobileMenu'))

// Mirrors the sticky-header heights and the scroll-down "hide" offset in
// _header.scss (.header--sticky, .header--hidden) - kept here so the space
// reserved below the header (.header--placeholder + headerOffsetVar, read by
// sticky sidebars) always matches what's actually visible, in every scroll state.
const MD_SCREEN = 899
const STICKY_HEIGHT = { mobile: 56, desktop: 90 }
const STICKY_HEIGHT_WHOLESALE = { mobile: 101, desktop: 115 }
const HIDDEN_OFFSET = { mobile: 60, desktop: 60 }
const HIDDEN_OFFSET_WHOLESALE_MOBILE = 101
// Never shrink the reserved space this close to the bottom of the page: on a short
// page (Cart/Checkout), shrinking would drop the document's scrollHeight below the
// current scroll position, which makes the browser clamp scrollY back up on its own -
// that clamp fires a 'scroll' event, useScrollDirection reads it as "scrolled up", the
// header un-hides, the space grows back, and the whole thing repeats forever (visible
// as the page jumping up/down). Comfortably larger than the biggest offset above.
// ENTER/EXIT are deliberately different (hysteresis): a single hard threshold flickers
// on/off from ordinary sub-pixel scroll jitter whenever the scroll position sits still
// right on top of it - that flicker was visible as the right-hand sticky panel jumping.
const SCROLL_HIDE_ENTER_PX = 200
const SCROLL_HIDE_EXIT_PX = 140
const BOTTOM_GUARD_ENTER_PX = 200
const BOTTOM_GUARD_EXIT_PX = 120
// Same reasoning applies one level up, to the plain "sticky vs not sticky" collapse
// (header--sticky, ~90px tall vs its natural in-flow size): a bare `scrollY > 0` has
// no dead zone, so rubber-band/overscroll bounce at the very top of the page - which
// briefly wobbles scrollY around 0 - flips it repeatedly, jumping the header itself
// between its two sizes and, through headerOffsetVar, the right-hand sticky panel.
const STICKY_ENTER_PX = 5
const STICKY_EXIT_PX = 0

const HeaderBase = () => {
  const history = useHistory()
  const location = useLocation()
  const { enqueueSnackbar } = useSnackbar()
  const scrollY = useScrollPosition(60)
  const scrollDir = useScrollDirection()
  const [, setParams] = useQueryParams(GlobalDialogFormsParams)
  const parsed = qs.parse(history.location.search, {
    ignoreQueryPrefix: true,
  })
  const [isActiveOverlay, setIsActiveOverlay] = useState(false)
  const [, setSearchActive, getSearchActive] = useLocalStorage<boolean>(LOCAL_STORAGE_KEYS.SEARCH_ACTIVE, false)
  const isWholesaleMode = useReactiveVar(isWholesaleModeVar)
  const [, forceResizeRecompute] = useState(0)
  const cartQuery = useCartQuery({
    context: { notBatching: true },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: { cartType: isWholesaleMode ? CartTypeEnum.Wholesale : CartTypeEnum.Default, includeTotalPrice: false },
  })

  const { data: userData, loading: userLoading } = useMeQuery()
  const [searchData, setSearchData] = React.useState((parsed && parsed.q) || '')
  const [, setGridClass] = useLocalStorage<REFLECTIONS_IMG_SIZE_ENUM>(STORAGE_GRID_NAME, DEFAULT_IMG_SIZE)

  useEffect(() => {
    if (userData?.me?.pk) {
      Cookies.remove(COOKIE_NAMES.CART)
    } else {
      if (cartQuery.data?.cart) {
        Cookies.set(COOKIE_NAMES.CART, cartQuery.data?.cart.cookieToken, { sameSite: 'lax' })
      }
    }
  }, [cartQuery.data?.cart, userData?.me])
  useEffect(() => {
    if (!!parsed && !!parsed.q) {
      setSearchActive(!!parsed && !!parsed.q)
    }
  }, [])

  useEffect(() => {
    if (window.dataLayer && !userLoading) {
      window.dataLayer.push({
        event: 'set-user-email',
        email: userData?.me?.email || '',
      })
    }
  }, [userData])

  useEffect(() => {
    if (!userLoading && !userData?.me) {
      isWholesaleModeVar(false)
      localStorage.removeItem(LOCAL_STORAGE_KEYS.WHOLESALE_MODE)
    }
  }, [userData, userLoading])

  useEffect(() => {
    if (userData?.me?.productListImageSize && window.localStorage.getItem(STORAGE_GRID_NAME) === null) {
      setGridClass(BACKEND_ENUM_TO_IMG_SIZE[userData.me.productListImageSize])
    }
  }, [userData?.me?.productListImageSize])

  useEffect(() => {
    const q = (parsed && parsed.q) || ''
    if (!q) {
      setSearchData('')
    }
    setSearchActive(false)
  }, [location.pathname])

  const onSubmitSearch = (data: { q: string; categoryName: string; categorySlug: string; categoryId: number }) => {
    setSearchData(data.q)
    const search = qs.stringify(
      { q: data.q, categories: data.categoryId ? [`${data.categorySlug}_${data.categoryId}`] : [] },
      {
        addQueryPrefix: true,
        arrayFormat: 'repeat',
      }
    )
    setSearchActive(false)
    history.push({
      pathname: SEARCH_URL,
      search,
    })
  }

  const changeStatusSearchBar = () => {
    setSearchActive(!getSearchActive())
  }

  const handleClickBag = e => {
    e.preventDefault()
    enqueueSnackbar('Your bag is empty', {
      variant: 'info',
    })
  }
  const handleOpenSignInDialog = e => {
    e.preventDefault()
    setParams({ global_dialog_form: 'signIn' }, 'replaceIn')
  }
  const handleClickWSMode = () => {
    isWholesaleModeVar(!isWholesaleMode)
    if (!isWholesaleMode) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.WHOLESALE_MODE, 'true')
    } else {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.WHOLESALE_MODE)
    }
  }
  const wasStickyRef = useRef(false)
  const wasHiddenRef = useRef(false)
  const wasSticky = wasStickyRef.current
  const isSticky = scrollY > (wasSticky ? STICKY_EXIT_PX : STICKY_ENTER_PX)
  wasStickyRef.current = isSticky
  // Entering/leaving sticky mode swaps the header between position:relative (natural,
  // larger size) and position:fixed (compact) - neither is animatable, so it's an
  // instant cut on the header's own side. Only animate the placeholder/panel offset
  // for changes that happen *within* sticky mode (the hidden/shown slide, which the
  // header itself does animate via its `top` transition) - animating the placeholder
  // across the sticky/non-sticky cut instead makes it lag the header's instant jump,
  // briefly double-reserving space and producing a hard bounce right at the very top.
  const animateReservedHeight = isSticky && wasSticky

  useEffect(() => {
    const onResize = () => forceResizeRecompute(n => n + 1)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  let isHidden = false
  let reservedHeight = 0
  if (isSticky) {
    const wasHidden = wasHiddenRef.current
    // Once hidden, require scrolling back up past the (lower) exit threshold rather
    // than the (higher) enter one - and vice versa. A single shared threshold flickers
    // on/off from ordinary sub-pixel scroll jitter whenever the scroll position sits
    // still right on top of it, which showed up as the right-hand sticky panel jumping.
    const pastScrollThreshold = scrollY > (wasHidden ? SCROLL_HIDE_EXIT_PX : SCROLL_HIDE_ENTER_PX)
    const wantsHidden = scrollDir === ScrollDirection.DOWN && pastScrollThreshold

    const isMobile = window.innerWidth <= MD_SCREEN
    const stickyHeights = isWholesaleMode ? STICKY_HEIGHT_WHOLESALE : STICKY_HEIGHT
    const stickyHeight = isMobile ? stickyHeights.mobile : stickyHeights.desktop
    const hiddenOffsetFull = isMobile && isWholesaleMode ? HIDDEN_OFFSET_WHOLESALE_MOBILE : isMobile ? HIDDEN_OFFSET.mobile : HIDDEN_OFFSET.desktop
    // Undo whatever shrink we ourselves last applied before measuring the distance to
    // the bottom - otherwise our own previous shrink/grow would throw off this render's
    // measurement and the guard below would trip on its own side effect.
    const previouslyReservedHeight = headerOffsetVar()
    const fullDocHeight = document.documentElement.scrollHeight - previouslyReservedHeight + stickyHeight
    const distanceFromBottom = fullDocHeight - (scrollY + window.innerHeight)
    const canHide = distanceFromBottom > (wasHidden ? BOTTOM_GUARD_EXIT_PX : BOTTOM_GUARD_ENTER_PX)

    isHidden = wantsHidden && canHide
    reservedHeight = Math.max(stickyHeight - (isHidden ? hiddenOffsetFull : 0), 0)
  }
  wasHiddenRef.current = isHidden

  useEffect(() => {
    headerOffsetVar(reservedHeight)
    headerOffsetAnimatedVar(animateReservedHeight)
  }, [reservedHeight, animateReservedHeight])

  return (
    <>
      <header className={`header w-full${isSticky ? ' header--sticky' : ''}${isHidden ? ' header--hidden' : ''}`}>
        {/*<PromoCodes hide={isSticky || isWholesaleMode} />*/}
        <div
          onClick={() => setIsActiveOverlay(false)}
          className={`wrapOverlayGlobal ${isActiveOverlay ? 'active' : ''}`}
        />
        <div className='relative' style={{ zIndex: 500 }}>
          <div className={` bg-white z-50`}>
            <div className='container'>
              {/*<Container maxWidth='xl' disableGutters>*/}
              <div className='mx-auto' style={{ maxWidth: '1680px' }}>
                <Grid container spacing={1}>
                  <Grid item>
                    <div className='flex items-center h-full z-10 relative'>
                      <Hidden lgUp>
                        <React.Suspense
                          fallback={
                            <div id='nav-icon'>
                              <span />
                              <span />
                              <span />
                              <span />
                            </div>
                          }
                        >
                          <MobileMenu />
                        </React.Suspense>
                        {getSearchActive() ? (
                          <CloseIcon onClick={changeStatusSearchBar} className='ml-20' />
                        ) : (
                          <SearchIcon onClick={changeStatusSearchBar} className='ml-20' />
                        )}
                      </Hidden>
                      <Hidden lgDown>
                        <HeaderLogo />
                      </Hidden>
                    </div>
                  </Grid>

                  <Grid item flexGrow={1}>
                    <Hidden lgUp>
                      <div className='flex h-full items-center'>
                        <HeaderLogo />
                      </div>
                    </Hidden>
                    <Hidden lgDown>
                      <div className='h-full items-center'>
                        <HeaderSearch onSubmitSearch={onSubmitSearch} initialQuery={(searchData as string) || ''} />
                      </div>
                    </Hidden>
                  </Grid>
                  <Grid item>
                    <div className={`header-utilities bg-white h-full relative z-10`}>
                      <HeaderUtilities
                        user={userData?.me}
                        cartCount={cartQuery.data?.cart?.count || 0}
                        handleClickSignIn={handleOpenSignInDialog}
                        isStaff={userData?.me?.isStaff}
                        handleClickBag={handleClickBag}
                        setIsActiveOverlay={setIsActiveOverlay}
                        handleClickContactUs={() => setParams({ global_dialog_form: 'contactUs' })}
                        handleClickWSMode={handleClickWSMode}
                        isWholesaleMode={isWholesaleMode}
                      />
                    </div>
                  </Grid>
                </Grid>
              </div>
              {/*</Container>*/}

              <HeaderMobileSearch
                onSubmitSearch={onSubmitSearch}
                isActive={getSearchActive()}
                initialQuery={(searchData as string) || ''}
                handleToggle={changeStatusSearchBar}
              />
              <Divider />
              <Menu setIsActiveOverlay={setIsActiveOverlay} />
            </div>
          </div>
        </div>
      </header>
      <div
        className='header--placeholder'
        style={{
          height: reservedHeight,
          overflowAnchor: 'none',
          transition: animateReservedHeight ? 'height 0.2s' : 'none',
        }}
      />
    </>
  )
}

export default React.memo(HeaderBase)
